import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const UserTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const trackUserNavigation = async () => {
      try {
        // Check if user is logged in
        const storedData = localStorage.getItem('userData');
        if (!storedData) {
          return;
        }

        let userData, token;
        
        try {
          // Try to parse as JSON first
          userData = JSON.parse(storedData);
          // Extract token from userData if it exists
          token = userData.token || userData.accessToken || storedData;
        } catch (e) {
          // If it's not JSON, it might be just a token string
          token = storedData;
          userData = {};
        }

        // Get user role and ID - we need to decode the token or get from localStorage
        let userId, userRole;
        
        if (userData.id || userData.IdUser) {
          // If we have user data from JSON parse
          userId = userData.id || userData.IdUser;
          userRole = userData.role;
        } else {
          // Try to decode JWT token to get role
          try {
            // JWT token format: header.payload.signature
            const payload = token.split('.')[1];
            if (payload) {
              const decodedPayload = JSON.parse(atob(payload));
              userId = decodedPayload.id || decodedPayload.IdUser;
              userRole = decodedPayload.role;
            }
          } catch (jwtError) {
            // Try to get role from separate localStorage item if it exists
            const userInfo = localStorage.getItem('userInfo');
            if (userInfo) {
              try {
                const parsedInfo = JSON.parse(userInfo);
                userId = parsedInfo.id || parsedInfo.IdUser;
                userRole = parsedInfo.role;
              } catch (parseError) {
                // Silent catch
              }
            }
          }
        }

        if (!userId || !userRole) {
          return;
        }

        // Map role to userType string
        let userType = '';
        switch(Number(userRole)) { // Ensure it's a number
          case 1: userType = 'admin'; break;
          case 2: userType = 'teacher'; break;
          case 3: userType = 'student'; break;
          case 4: userType = 'sales'; break;
          default: 
            return; // Silently skip for unknown roles
        }

        // Get IP address - simpler method without location
        let ipAddress = '';
        try {
          // Use a simple IP service that's more reliable
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          ipAddress = ipData.ip;
        } catch (error) {
          // Silently fail if IP fetch fails
          // We'll just continue without IP
        }

        // Get device type
        const userAgent = navigator.userAgent;
        let deviceType = 'desktop';
        if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
          deviceType = 'mobile';
        } else if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
          deviceType = 'tablet';
        }

        // Prepare tracking data
        const trackingData = {
          userId: String(userId),
          userType: userType,
          sessionId: localStorage.getItem('sessionId') || generateSessionId(),
          pageUrl: location.pathname + location.search,
          pagePath: location.pathname,
          pageTitle: document.title || '',
          referrer: document.referrer || '',
          userAgent: userAgent.substring(0, 500),
          deviceType: deviceType,
          ipAddress: ipAddress || ''
          // Remove country and city since we can't get them reliably
        };

        // Send to API
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/user-tracking`,
          trackingData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

      } catch (error) {
        // Fail silently - don't affect user experience
      }
    };

    // Small delay to ensure page is loaded
    const timeoutId = setTimeout(() => {
      trackUserNavigation();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search]);

  return null;
};

// Helper function to generate session ID
function generateSessionId() {
  let sessionId = localStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
}

export default UserTracking;